import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { getTheme } from '../../theme';
import Card from '../common/Card';
import { Person, Phone, Scale, Height, CalendarToday, FitnessCenter } from '@mui/icons-material';

const CardContainer = styled(Card)`
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;

  &:hover {
    transform: translateY(-4px);
    ${props => props.theme?.isDark ? `
      box-shadow: 0 8px 20px rgba(25, 184, 136, 0.3);
    ` : `
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
    `}
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.md};
  padding-bottom: ${props => getTheme(props.theme?.isDark).spacing.md};
  border-bottom: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
`;

const AvatarContainer = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  background: ${props => getTheme(props.theme?.isDark).colors.surface};
  border: 2px solid ${props => getTheme(props.theme?.isDark).colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  ${props => props.theme?.isDark && `
    box-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
  `}
`;

const Avatar = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => getTheme(props.theme?.isDark).colors.primary}20;
  color: ${props => getTheme(props.theme?.isDark).colors.primary};
  font-size: 24px;
  font-weight: 700;
`;

const CustomerInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CustomerName = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin: 0 0 ${props => getTheme(props.theme?.isDark).spacing.xs} 0;
  ${props => props.theme?.isDark && `
    text-shadow: 0 0 5px rgba(25, 184, 136, 0.3);
  `}
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CustomerEmail = styled.div`
  font-size: 14px;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xs} ${props => getTheme(props.theme?.isDark).spacing.sm};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.sm};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  background-color: ${props => {
    const status = props.status?.toLowerCase();
    if (status === 'active') return getTheme(props.theme?.isDark).colors.success + '20';
    if (status === 'completed') return getTheme(props.theme?.isDark).colors.info + '20';
    return getTheme(props.theme?.isDark).colors.secondary + '20';
  }};
  color: ${props => {
    const status = props.status?.toLowerCase();
    if (status === 'active') return getTheme(props.theme?.isDark).colors.success;
    if (status === 'completed') return getTheme(props.theme?.isDark).colors.info;
    return getTheme(props.theme?.isDark).colors.secondary;
  }};
  ${props => props.theme?.isDark && props.status?.toLowerCase() === 'active' && `
    box-shadow: 0 0 8px rgba(25, 184, 136, 0.3);
  `}
`;

const CardBody = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
  font-size: 14px;
`;

const InfoIcon = styled.div`
  color: ${props => getTheme(props.theme?.isDark).colors.primary};
  display: flex;
  align-items: center;
  font-size: 18px;
`;

const InfoLabel = styled.span`
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-weight: 600;
`;

const WeightProgress = styled.div`
  margin-top: ${props => getTheme(props.theme?.isDark).spacing.md};
  padding-top: ${props => getTheme(props.theme?.isDark).spacing.md};
  border-top: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.xs};
  font-size: 12px;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
`;

const ProgressBar = styled.div`
  height: 8px;
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.sm};
  overflow: hidden;
  position: relative;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, ${props => getTheme(props.theme?.isDark).colors.primary}, ${props => getTheme(props.theme?.isDark).colors.primaryLight});
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.sm};
  transition: width 0.3s ease;
  ${props => props.theme?.isDark && `
    box-shadow: 0 0 10px rgba(25, 184, 136, 0.5);
  `}
`;

const CustomerCard = ({ customer }) => {
  const { isDark } = useTheme();
  const theme = { isDark };
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/admin/customers/${customer.id}`);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getAvatarUrl = () => {
    if (customer.avatarPath) return customer.avatarPath;
    if (customer.avatar) {
      const baseUrl = (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') 
        ? 'http://127.0.0.1:3000' 
        : 'https://api.application.fit';
      return `${baseUrl}${customer.avatar}`;
    }
    return null;
  };

  const calculateWeightProgress = () => {
    if (!customer.weight || !customer.weightTo) return 0;
    // If target is less than current (weight loss)
    if (customer.weightTo < customer.weight) {
      // For now, we'll show a simple progress - can be enhanced with actual progress tracking
      return 50; // Placeholder - should be calculated based on actual progress
    }
    // If target is more than current (weight gain)
    return 0;
  };

  const weightProgress = calculateWeightProgress();

  return (
    <CardContainer theme={theme} onClick={handleClick}>
      <CardHeader theme={theme}>
        <AvatarContainer theme={theme}>
          {getAvatarUrl() ? (
            <Avatar src={getAvatarUrl()} alt={customer.name || 'Customer'} />
          ) : (
            <AvatarPlaceholder theme={theme}>
              {getInitials(customer.name)}
            </AvatarPlaceholder>
          )}
        </AvatarContainer>
        <CustomerInfo>
          <CustomerName theme={theme}>{customer.name || 'No Name'}</CustomerName>
          <CustomerEmail theme={theme}>{customer.email || customer.phone || 'No contact'}</CustomerEmail>
        </CustomerInfo>
        {customer.status && (
          <StatusBadge theme={theme} status={customer.status}>
            {customer.status}
          </StatusBadge>
        )}
      </CardHeader>

      <CardBody theme={theme}>
        {customer.phone && (
          <InfoItem theme={theme}>
            <InfoIcon theme={theme}>
              <Phone fontSize="small" />
            </InfoIcon>
            <InfoLabel theme={theme}>Phone:</InfoLabel>
            <InfoValue theme={theme}>{customer.phone}</InfoValue>
          </InfoItem>
        )}
        
        {customer.gender && (
          <InfoItem theme={theme}>
            <InfoIcon theme={theme}>
              <Person fontSize="small" />
            </InfoIcon>
            <InfoLabel theme={theme}>Gender:</InfoLabel>
            <InfoValue theme={theme}>{customer.gender}</InfoValue>
          </InfoItem>
        )}

        {customer.weight && (
          <InfoItem theme={theme}>
            <InfoIcon theme={theme}>
              <Scale fontSize="small" />
            </InfoIcon>
            <InfoLabel theme={theme}>Weight:</InfoLabel>
            <InfoValue theme={theme}>{customer.weight} kg</InfoValue>
          </InfoItem>
        )}

        {customer.height && (
          <InfoItem theme={theme}>
            <InfoIcon theme={theme}>
              <Height fontSize="small" />
            </InfoIcon>
            <InfoLabel theme={theme}>Height:</InfoLabel>
            <InfoValue theme={theme}>{customer.height} cm</InfoValue>
          </InfoItem>
        )}

        {customer.birthdate && (
          <InfoItem theme={theme}>
            <InfoIcon theme={theme}>
              <CalendarToday fontSize="small" />
            </InfoIcon>
            <InfoLabel theme={theme}>Age:</InfoLabel>
            <InfoValue theme={theme}>
              {new Date().getFullYear() - new Date(customer.birthdate).getFullYear()}
            </InfoValue>
          </InfoItem>
        )}

        {customer.bmi && (
          <InfoItem theme={theme}>
            <InfoIcon theme={theme}>
              <FitnessCenter fontSize="small" />
            </InfoIcon>
            <InfoLabel theme={theme}>BMI:</InfoLabel>
            <InfoValue theme={theme}>{customer.bmi.toFixed(1)}</InfoValue>
          </InfoItem>
        )}
      </CardBody>

      {customer.weight && customer.weightTo && (
        <WeightProgress theme={theme}>
          <ProgressLabel theme={theme}>
            <span>Weight Goal</span>
            <span>{customer.weight} kg → {customer.weightTo} kg</span>
          </ProgressLabel>
          <ProgressBar theme={theme}>
            <ProgressFill 
              theme={theme} 
              style={{ width: `${weightProgress}%` }}
            />
          </ProgressBar>
        </WeightProgress>
      )}
    </CardContainer>
  );
};

export default CustomerCard;

